import React, { useState, useRef } from 'react';
import { ArrowRight, Check, Globe, Shield, Euro, Users, Building2, FileText, Plane, BadgeCheck, MapPin, Paperclip, X, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import SEOHead from './SEOHead';
import NewTopBar from './NewTopBar';
import NewHeader from './NewHeader';
import NewFooter from './NewFooter';
import ChatWidget from './ChatWidget';
import { CompanyTypesSection, InvestorFaqAndCta, InvestorProcessSection, InvestorServicesSection, InvestorStats, WhySloveniaSection } from './investors/InvestorContentSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 3 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx'];

const InvestorsPage = () => {
  const { language, setLanguage } = useLanguage();
  const sl = language === 'sl';
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    message: '',
    consent: false,
    honeypot: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const scrollToForm = () => {
    document.querySelector('#investor-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(sl ? 'Datoteka je prevelika. Največja dovoljena velikost je 3 MB.' : 'File is too large. Maximum allowed size is 3 MB.');
      return;
    }
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(sl ? 'Nepodprta vrsta datoteke. Dovoljene so: PDF, slike, Word in Excel.' : 'Unsupported file type. Allowed: PDF, images, Word and Excel.');
      return;
    }
    setSelectedFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    if (!formData.name || !formData.contact || !formData.consent) {
      toast.error(sl ? 'Prosim izpolnite vsa obvezna polja' : 'Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      let response;
      if (selectedFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('contact', formData.contact);
        formDataToSend.append('message', formData.message || '');
        formDataToSend.append('language', language);
        formDataToSend.append('file', selectedFile);
        response = await fetch(`${API_URL}/api/consultation-with-file`, { method: 'POST', body: formDataToSend });
      } else {
        response = await fetch(`${API_URL}/api/consultation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: formData.name, contact: formData.contact, language: language, timestamp: new Date().toISOString() })
        });
      }
      if (response.ok) {
        setIsSubmitted(true);
        toast.success(sl ? 'Hvala! Oglasili se vam bomo v 1 delovnem dnevu.' : 'Thank you! We will get back to you within 1 working day.');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error(error.message || (sl ? 'Prišlo je do napake. Prosim poskusite znova.' : 'An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Content translations
  const content = {
    seo: {
      title: sl 
        ? 'Ustanovitev podjetja v Sloveniji — Vodnik za investitorje | General Ledger'
        : 'Start a Business in Slovenia — Guide for Foreign Investors | General Ledger',
      description: sl
        ? 'Celoten vodnik za tuje podjetnike. Odprite podjetje v Sloveniji v 7-14 dneh. Dostop do EU, nizki davki, podpora v angleščini. Brezplačen posvet.'
        : 'Complete guide for foreign entrepreneurs. Open a company in Slovenia in 7-14 days. EU access, low taxes, English-speaking support. Free consultation.'
    },
    hero: {
      tagline: sl ? 'VODNIK ZA INVESTITORJE' : 'GUIDE FOR FOREIGN INVESTORS',
      title: sl ? 'Začnite svoje poslovanje v ' : 'Start your business in ',
      titleHighlight: sl ? 'Sloveniji, EU' : 'Slovenia, EU',
      description: sl
        ? 'Vaša pot do evropskega trga. Ustanovitev podjetja v 7-14 dneh, konkurenčne davčne stopnje in predana ekipa, ki govori vaš jezik.'
        : 'Your gateway to the European market. Company formation in 7-14 days, competitive tax rates, and a dedicated English-speaking team by your side.',
      cta: sl ? 'Rezervirajte brezplačen posvet' : 'Book free consultation',
      secondary: sl ? 'Zakaj Slovenija?' : 'Why Slovenia?'
    },
    stats: [
      { value: '20+', label: sl ? 'let izkušenj' : 'years of experience', sublabel: sl ? 'Od leta 2003' : 'Since 2003' },
      { value: '500+', label: sl ? 'ustanovljenih podjetij' : 'companies formed', sublabel: sl ? 'Za stranke po svetu' : 'For clients worldwide' },
      { value: '7-14', label: sl ? 'dni za registracijo' : 'days to register', sublabel: sl ? 'Celotna storitev' : 'Full-service setup' },
      { value: '4', label: sl ? 'jeziki' : 'languages spoken', sublabel: 'EN, SL, HR, ES' },
    ],
    whySlovenia: {
      tagline: sl ? 'ZAKAJ SLOVENIJA' : 'WHY SLOVENIA',
      title: sl ? 'Strateška lokacija s ' : 'A strategic location with ',
      titleHighlight: sl ? 'konkurenčnimi prednostmi' : 'competitive advantages',
      items: [
        {
          icon: Globe,
          title: sl ? 'Članica EU in Schengena' : 'EU & Schengen Member',
          desc: sl ? 'Poln dostop do evropskega enotnega trga z več kot 450 milijoni potrošnikov. Prosta izmenjava blaga, storitev in kapitala.' : 'Full access to the European single market of 450+ million consumers. Free movement of goods, services, and capital.'
        },
        {
          icon: Euro,
          title: sl ? '22% davek na dobiček' : '22% Corporate Tax',
          desc: sl ? 'Konkurenčna davčna stopnja (19% od leta 2028). Dodatne spodbude za R&D, investicije in zaposlovanje.' : 'Competitive tax rate (19% from 2028). Additional incentives for R&D, investments, and hiring.'
        },
        {
          icon: MapPin,
          title: sl ? 'Strateška lokacija' : 'Strategic Location',
          desc: sl ? 'Na stičišču zahodne in vzhodne Evrope. Enostaven dostop do Avstrije, Italije, Madžarske in Hrvaške.' : 'At the crossroads of Western and Eastern Europe. Easy access to Austria, Italy, Hungary, and Croatia.'
        },
        {
          icon: Users,
          title: sl ? 'Usposobljena delovna sila' : 'Skilled Workforce',
          desc: sl ? 'Visoko izobraženo, večjezično prebivalstvo. Močna delovna etika in konkurenčni stroški dela v primerjavi z zahodno Evropo.' : 'Highly educated, multilingual population. Strong work ethic and competitive labor costs compared to Western Europe.'
        },
        {
          icon: Shield,
          title: sl ? 'Stabilno gospodarstvo' : 'Stable Economy',
          desc: sl ? 'Članica evroobmočja od leta 2007. Močan bančni sistem, pravna država in transparentno poslovno okolje.' : 'Eurozone member since 2007. Strong banking system, rule of law, and transparent business environment.'
        },
        {
          icon: Building2,
          title: sl ? 'Poslovno prijazno' : 'Business-Friendly',
          desc: sl ? 'Med najlažjimi mesti za poslovanje v vzhodni Evropi. Hitra registracija podjetij in pregledna zakonodaja.' : 'Ranked among the easiest places to do business in Eastern Europe. Fast company registration and transparent legislation.'
        }
      ]
    },
    companyTypes: {
      tagline: sl ? 'VRSTE PODJETIJ' : 'COMPANY TYPES',
      title: sl ? 'Izberite pravo strukturo za ' : 'Choose the right structure for ',
      titleHighlight: sl ? 'vaše poslovanje' : 'your business',
      description: sl
        ? 'Pomagamo vam izbrati in registrirati optimalno pravno obliko glede na vaš poslovni model, lastniško strukturo in načrte rasti.'
        : 'We help you select and register the optimal legal entity based on your business model, ownership structure, and growth plans.',
      items: [
        {
          type: 'd.o.o.',
          name: sl ? 'Družba z omejeno odgovornostjo' : 'Limited Liability Company',
          capital: sl ? '€7.500 osnovni kapital' : '€7,500 minimum capital',
          best: sl ? 'Najpogostejša izbira za tuje investitorje. Omejena odgovornost, fleksibilno upravljanje, kredibilnost pri partnerjih.' : 'Most popular choice for foreign investors. Limited liability, flexible management, credibility with partners.',
          time: sl ? '7-14 dni' : '7-14 days'
        },
        {
          type: 's.p.',
          name: sl ? 'Samostojni podjetnik' : 'Sole Proprietorship',
          capital: sl ? 'Brez osnovnega kapitala' : 'No minimum capital',
          best: sl ? 'Za freelancerje, svetovalce in mala storitvena podjetja. Enostavna ustanovitev, vendar osebna odgovornost.' : 'Freelancers, consultants, and small service businesses. Simple setup, but personal liability.',
          time: sl ? '3-5 dni' : '3-5 days'
        },
        {
          type: sl ? 'Podružnica' : 'Branch',
          name: sl ? 'Podružnica tujega podjetja' : 'Branch Office',
          capital: sl ? 'Brez ločenega kapitala' : 'No separate capital',
          best: sl ? 'Razširitev vašega obstoječega tujega podjetja. Dobra opcija za testiranje trga pred polno zavezanostjo.' : 'Extension of your existing foreign company. Good for testing the market before full commitment.',
          time: sl ? '10-20 dni' : '10-20 days'
        }
      ]
    },
    process: {
      tagline: sl ? 'KAKO POTEKA' : 'HOW IT WORKS',
      title: sl ? 'Od prvega klica do ' : 'From first call to ',
      titleHighlight: sl ? 'delujočega podjetja' : 'operating company',
      steps: [
        {
          step: '01',
          title: sl ? 'Brezplačen posvet' : 'Free Consultation',
          desc: sl ? 'Pogovorimo se o vaših poslovnih načrtih, priporočimo najboljšo strukturo in pripravimo fiksno ponudbo. Brez obveznosti.' : 'We discuss your business plans, recommend the best structure, and provide a fixed-price quote. No obligations.',
          time: '30 min'
        },
        {
          step: '02',
          title: sl ? 'Davčna številka in dokumenti' : 'Tax Number & Documents',
          desc: sl ? 'Najprej pridobimo vašo davčno številko pri FURS. Nato pripravimo vso ustanovitveno dokumentacijo. Najlažji je kratek obisk Slovenije: podjetje lahko odprete na točki eVEM, lahko pa ga za vas odpremo tudi mi in po vašem prihodu v Slovenijo izvedemo prenos.' : 'First, we obtain your tax number from FURS. Then we prepare all incorporation documents. The easiest option is a short visit to Slovenia: you can open the company at an eVEM point, or we can establish it for you and complete the transfer after your arrival in Slovenia.',
          time: sl ? '2-3 dni' : '2-3 days'
        },
        {
          step: '03',
          title: sl ? 'Registracija' : 'Registration',
          desc: sl ? 'Registriramo vaše podjetje pri sodišču, davčnem uradu in statističnem uradu. Prejmete registracijsko številko.' : 'We register your company with the court, tax authority, and statistical office. You receive your registration number.',
          time: sl ? '5-10 dni' : '5-10 days'
        },
        {
          step: '04',
          title: sl ? 'Banka in poslovanje' : 'Bank & Operations',
          desc: sl ? 'Pomagamo odpreti poslovni bančni račun in vzpostaviti računovodstvo. Pripravljeni ste za izdajanje računov v EU.' : 'We help open a business bank account and set up accounting. You are ready to invoice and operate in the EU.',
          time: sl ? '1-2 tedna' : '1-2 weeks'
        }
      ]
    },
    services: {
      tagline: sl ? 'KAJ JE VKLJUČENO' : 'WHAT IS INCLUDED',
      title: sl ? 'Vse, kar potrebujete za ' : 'Everything you need to ',
      titleHighlight: sl ? 'zagon in vodenje' : 'start and run',
      titleEnd: sl ? ' podjetja' : ' your business',
      formation: {
        tagline: sl ? 'USTANOVITEV PODJETJA' : 'COMPANY FORMATION',
        title: sl ? 'Enkratna vzpostavitev' : 'One-time setup',
        items: sl ? [
          'Pridobitev davčne številke ustanovitelja',
          'Registracija podjetja pri vseh organih',
          'Registracija davčne številke (DDV)',
          'Sedež podjetja (1 leto)',
          'Pomoč pri odpiranju bančnega računa',
          'Začetni davčni in pravni posvet'
        ] : [
          'Obtaining founder\'s tax number',
          'Company registration with all authorities',
          'Tax number (VAT) registration',
          'Registered office address (1 year)',
          'Bank account opening assistance',
          'Initial tax and legal consultation'
        ]
      },
      ongoing: {
        tagline: sl ? 'TEKOČA PODPORA' : 'ONGOING SUPPORT',
        title: sl ? 'Mesečne storitve' : 'Monthly services',
        items: sl ? [
          'Celotno knjigovodstvo in računovodstvo',
          'Mesečni DDV obračuni',
          'Obračun plač',
          'Davčno svetovanje',
          'Neomejena podpora po e-pošti in telefonu',
          'Hramba in arhiviranje dokumentov'
        ] : [
          'Full bookkeeping & accounting',
          'Monthly VAT returns',
          'Payroll processing',
          'Tax optimization advice',
          'Unlimited email & phone support',
          'Document storage & archiving'
        ]
      },
      additional: {
        tagline: sl ? 'DODATNE STORITVE' : 'ADDITIONAL SERVICES',
        items: [
          { icon: FileText, title: sl ? 'Delovna dovoljenja' : 'Work Permits', desc: sl ? 'Enotna dovoljenja za zaposlene iz tretjih držav' : 'Single permits for non-EU employees' },
          { icon: Plane, title: sl ? 'Bivalna dovoljenja' : 'Residence Permits', desc: sl ? 'Za ustanovitelje in ključno osebje' : 'For founders and key personnel' },
          { icon: BadgeCheck, title: sl ? 'Vračilo DDV iz EU' : 'EU VAT Refunds', desc: sl ? 'Povračilo DDV, plačanega v drugih državah EU' : 'Recover VAT paid in other EU states' },
          { icon: Building2, title: sl ? 'Virtualna pisarna' : 'Virtual Office', desc: sl ? 'Prestižen naslov in upravljanje pošte' : 'Prestigious address & mail handling' }
        ]
      }
    },
    faq: {
      tagline: sl ? 'POGOSTA VPRAŠANJA' : 'FAQ FOR INVESTORS',
      title: sl ? 'Pogosta vprašanja' : 'Common questions',
      items: sl ? [
        {
          q: 'Ali moram priti v Slovenijo za registracijo podjetja?',
          a: 'Priporočamo kratek obisk Slovenije (1-2 dni), saj je to najhitrejši in najpreprostejši način. Pred ustanovitvijo je potrebno pridobiti slovensko davčno številko, nato pa podpisati dokumente pri notarju. Lahko pa uredimo tudi na daljavo prek slovenskega veleposlaništva ali apostille, vendar je postopek daljši.'
        },
        {
          q: 'Kolikšen je minimalni kapital za d.o.o.?',
          a: 'Minimalni osnovni kapital za d.o.o. je 7.500 €. Če imate enega ustanovitelja, je treba ob registraciji vplačati vseh 7.500 €. Kapital se lahko vplača na začasni račun med registracijo.'
        },
        {
          q: 'Ali sem lahko direktor lastnega podjetja kot državljan tretje države?',
          a: 'Da. Državljani tretjih držav so lahko direktorji. Če pa nameravate delati v Sloveniji, boste potrebovali delovno in bivalno dovoljenje. Pomagamo pri celotnem postopku pridobivanja dovoljenj.'
        },
        {
          q: 'Koliko časa traja odprtje poslovnega bančnega računa?',
          a: 'Običajno 1-2 tedna po registraciji podjetja. Nekatere banke ponujajo odprtje računa na daljavo za državljane EU. Državljani tretjih držav morajo običajno enkrat obiskati banko osebno.'
        },
        {
          q: 'Kakšne so tekoče obveznosti skladnosti?',
          a: 'Mesečni ali četrtletni DDV obračuni (če ste registrirani za DDV), letni računovodski izkazi, davčna napoved in poročila o plačah, če imate zaposlene. Vse to uredimo za vas.'
        },
        {
          q: 'Ali je Slovenija del evroobmočja?',
          a: 'Da, Slovenija je sprejela evro leta 2007. Vsi poslovni posli, fakturiranje in bančništvo so v EUR, kar poenostavlja poslovanje po EU.'
        }
      ] : [
        {
          q: 'Do I need to visit Slovenia to register a company?',
          a: 'We recommend a short visit to Slovenia (1-2 days) as it is the fastest and simplest way. Before incorporation, you need to obtain a Slovenian tax number, then sign documents at a notary. Remote setup via Slovenian embassy or apostille is possible but takes longer.'
        },
        {
          q: 'What is the minimum capital required for a d.o.o.?',
          a: 'The minimum share capital for a d.o.o. is €7,500. However, only €7,500 needs to be paid in at registration if you have a single founder. The capital can be paid to a temporary account during registration.'
        },
        {
          q: 'Can I be the director of my own company as a non-EU citizen?',
          a: 'Yes. Non-EU citizens can be directors. However, if you plan to work in Slovenia, you will need a work and residence permit. We assist with the entire permit process.'
        },
        {
          q: 'How long does it take to open a business bank account?',
          a: 'Typically 1-2 weeks after company registration. Some banks offer remote account opening for EU citizens. Non-EU citizens usually need to visit the bank in person once.'
        },
        {
          q: 'What are the ongoing compliance requirements?',
          a: 'Monthly or quarterly VAT returns (if registered for VAT), annual financial statements, corporate tax return, and payroll reports if you have employees. We handle all of this for you.'
        },
        {
          q: 'Is Slovenia part of the Eurozone?',
          a: 'Yes, Slovenia adopted the Euro in 2007. All business transactions, invoicing, and banking are in EUR, simplifying operations across the EU.'
        }
      ]
    },
    form: {
      tagline: sl ? 'BREZPLAČNO ZA NOVE STRANKE' : 'FREE FOR NEW CLIENTS',
      subtitle: sl ? 'Brezplačno · brez obveznosti' : 'Free · no obligation',
      title: sl ? 'Rezervirajte brezplačen posvet' : 'Book your free consultation',
      description: sl 
        ? 'Pogovor z izkušenim svetovalcem — ne s prodajalcem. Prejmete jasen načrt in fiksno ponudbo v 48 urah.'
        : 'A conversation with an experienced advisor — not a salesperson. Get a clear plan and fixed quote within 48 hours.',
      benefits: sl ? [
        'Pravno obliko in ureditev, pri kateri plačate najmanj davka',
        'Seznam vseh registracij, dovoljenj in rokov',
        'Fiksno pisno ponudbo v 48 urah'
      ] : [
        'Legal form and setup where you pay the least tax',
        'List of all registrations, permits and deadlines',
        'Fixed written quote within 48 hours'
      ],
      nameLabel: sl ? 'Ime in priimek' : 'Full name',
      contactLabel: sl ? 'E-pošta ali telefon' : 'Email or phone',
      messageLabel: sl ? 'Opišite vaš projekt (neobvezno)' : 'Describe your project (optional)',
      messagePlaceholder: sl ? 'Na kratko opišite vaše poslovne načrte...' : 'Briefly describe your business plans...',
      fileLabel: sl ? 'Priponka (neobvezno)' : 'Attachment (optional)',
      filePlaceholder: sl ? 'Dodaj PDF, sliko ali dokument (max 3 MB)' : 'Add PDF, image or document (max 3 MB)',
      consent: sl 
        ? 'Strinjam se z obdelavo osebnih podatkov v skladu s'
        : 'I agree to the processing of my personal data in line with the',
      privacyLink: sl ? 'politiko zasebnosti' : 'privacy policy',
      submit: sl ? 'Pošlji povpraševanje' : 'Submit inquiry',
      submitting: sl ? 'Pošiljam...' : 'Submitting...',
      success: {
        title: sl ? 'Hvala!' : 'Thank you!',
        message: sl ? 'Oglasili se vam bomo v 1 delovnem dnevu.' : 'We will get back to you within 1 working day.'
      }
    },
    cta: {
      tagline: sl ? 'PRIPRAVLJENI NA ZAČETEK?' : 'READY TO START?',
      title: sl ? 'Rezervirajte brezplačen posvet ' : 'Book your free consultation ',
      titleHighlight: sl ? 'še danes' : 'today',
      description: sl 
        ? 'Prejmite osebno oceno vaše situacije, jasen načrt in fiksno ponudbo — vse v 48 urah. Brez obveznosti, brez skritih stroškov.'
        : 'Get a personalized assessment of your situation, a clear roadmap, and a fixed-price quote — all within 48 hours. No obligations, no hidden fees.',
      button: sl ? 'Rezerviraj posvet' : 'Schedule consultation',
      email: sl ? 'Ali nam pišite na' : 'Or email us directly at'
    }
  };

  const investorJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": content.seo.title,
    "description": content.seo.description,
    "url": `https://general-ledger.eu/${sl ? 'investitorji' : 'investors'}`,
    "inLanguage": sl ? "sl" : "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "General Ledger",
      "url": "https://general-ledger.eu"
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F2EA]">
      <SEOHead
        title={content.seo.title}
        description={content.seo.description}
        canonicalPath={sl ? "/investitorji" : "/investors"}
        lang={sl ? "sl" : "en"}
        alternateUrl={sl ? "/investors" : "/investitorji"}
        jsonLd={investorJsonLd}
      />
      <NewTopBar />
      <NewHeader />

      {/* Hero Section */}
      <section className="bg-[#0E1114] text-[#F4EFE6] py-20 lg:py-32 px-6 lg:px-[120px]">
        <div className="max-w-[1440px] mx-auto">
          {/* Language Switcher */}
          <div className="flex justify-end mb-8">
            <div className="inline-flex border border-[#454C54] rounded-sm overflow-hidden">
              <button
                onClick={() => setLanguage('sl')}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${sl ? 'bg-[#C9A462] text-[#0E1114]' : 'text-[#C9C3B7] hover:text-white'}`}
                data-testid="lang-switch-sl"
              >
                SL
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${!sl ? 'bg-[#C9A462] text-[#0E1114]' : 'text-[#C9C3B7] hover:text-white'}`}
                data-testid="lang-switch-en"
              >
                EN
              </button>
            </div>
          </div>
          <div className="max-w-[900px]">
            <span className="text-[13px] font-bold tracking-[2.5px] text-[#C9A462] block mb-5">
              {content.hero.tagline}
            </span>
            <h1 className="font-serif text-[42px] sm:text-[56px] lg:text-[72px] leading-[1.02] tracking-[-1.5px] mb-6">
              {content.hero.title}<span className="italic text-[#C9A462]">{content.hero.titleHighlight}</span>
            </h1>
            <p className="text-xl lg:text-2xl text-[#C9C3B7] leading-[1.5] mb-8 max-w-[700px]">
              {content.hero.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToForm}
                className="flex items-center gap-3 bg-[#C9A462] text-[#0E1114] font-bold text-lg px-8 py-5 hover:bg-[#B8944F] transition-colors"
                data-testid="investors-cta-main"
              >
                {content.hero.cta}
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#why-slovenia"
                className="flex items-center gap-3 border border-[#454C54] text-[#F4EFE6] font-semibold text-lg px-8 py-5 hover:bg-[#1a1d21] transition-colors"
              >
                {content.hero.secondary}
              </a>
            </div>
          </div>
        </div>
      </section>

      <InvestorStats stats={content.stats} />
      <WhySloveniaSection content={content.whySlovenia} />
      <CompanyTypesSection content={content.companyTypes} />
      <InvestorProcessSection content={content.process} sl={sl} />

      <InvestorServicesSection content={content.services} />

      {/* Consultation Form Section */}
      <section id="investor-form" className="py-20 lg:py-28 px-6 lg:px-[120px] bg-[#0E1114] text-[#F4EFE6]">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            {/* Left - Info */}
            <div className="flex-1">
              <span className="text-[13px] font-bold tracking-[2.5px] text-[#C9A462] block mb-4">
                {content.form.tagline}
              </span>
              <h2 className="font-serif text-3xl lg:text-[56px] leading-[1.05] tracking-[-1px] mb-6">
                {content.form.title}
              </h2>
              <p className="text-lg text-[#C9C3B7] mb-8 max-w-[500px]">
                {content.form.description}
              </p>
              <div className="space-y-4 mb-8">
                {content.form.benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#C9A462] flex-shrink-0 mt-0.5" />
                    <span className="text-[#D8D2C6]">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-[#7E786D]">
                <Phone className="w-5 h-5" />
                <a href="tel:+38638184060" className="text-[#C9A462] hover:underline">+386 3 818 40 60</a>
              </div>
            </div>

            {/* Right - Form */}
            <div className="w-full lg:w-[480px] flex-shrink-0">
              <div className="bg-[#F6F2EA] text-[#15181C] p-8 lg:p-10 border-t-4 border-[#C9A462]">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[#C9A462] rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-[#0E1114]" />
                    </div>
                    <h3 className="font-serif text-[28px] mb-4">{content.form.success.title}</h3>
                    <p className="text-[#4E5359] text-[15px]">{content.form.success.message}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData(prev => ({ ...prev, honeypot: e.target.value }))} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    
                    <div>
                      <label className="text-[12px] font-semibold text-[#4E5359] block mb-1.5" htmlFor="inv-name">
                        {content.form.nameLabel} *
                      </label>
                      <input
                        id="inv-name"
                        type="text"
                        placeholder={content.form.nameLabel}
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full h-12 border border-[#CFC7B8] bg-[#FFFDF8] px-3.5 text-[15px] font-[inherit] focus:outline-none focus:border-[#C9A462]"
                        data-testid="investor-form-name"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[12px] font-semibold text-[#4E5359] block mb-1.5" htmlFor="inv-contact">
                        {content.form.contactLabel} *
                      </label>
                      <input
                        id="inv-contact"
                        type="text"
                        placeholder={sl ? 'vi@podjetje.si' : 'you@company.com'}
                        value={formData.contact}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                        required
                        className="w-full h-12 border border-[#CFC7B8] bg-[#FFFDF8] px-3.5 text-[15px] font-[inherit] focus:outline-none focus:border-[#C9A462]"
                        data-testid="investor-form-contact"
                      />
                    </div>

                    <div>
                      <label className="text-[12px] font-semibold text-[#4E5359] block mb-1.5" htmlFor="inv-message">
                        {content.form.messageLabel}
                      </label>
                      <textarea
                        id="inv-message"
                        placeholder={content.form.messagePlaceholder}
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        rows={3}
                        className="w-full border border-[#CFC7B8] bg-[#FFFDF8] px-3.5 py-2.5 text-[15px] font-[inherit] focus:outline-none focus:border-[#C9A462] resize-none"
                        data-testid="investor-form-message"
                      />
                    </div>

                    <div>
                      <label className="text-[12px] font-semibold text-[#4E5359] block mb-1.5">
                        {content.form.fileLabel}
                      </label>
                      <div className="relative">
                        <input ref={fileInputRef} type="file" onChange={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx" className="hidden" id="inv-file" data-testid="investor-form-file" />
                        {selectedFile ? (
                          <div className="flex items-center justify-between h-12 border border-[#C9A462] bg-[#FFFDF8] px-3.5">
                            <span className="text-[14px] text-[#15181C] truncate flex-1">{selectedFile.name}</span>
                            <button type="button" onClick={removeFile} className="ml-2 text-[#4E5359] hover:text-[#15181C] transition-colors" aria-label={sl ? 'Odstrani datoteko' : 'Remove file'}>
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <label htmlFor="inv-file" className="flex items-center justify-center gap-2 h-12 border border-dashed border-[#CFC7B8] bg-[#FFFDF8] px-3.5 cursor-pointer hover:border-[#C9A462] transition-colors">
                            <Paperclip className="w-4 h-4 text-[#7A5B28]" />
                            <span className="text-[14px] text-[#4E5359]">{content.form.filePlaceholder}</span>
                          </label>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <input
                        type="checkbox"
                        id="inv-consent"
                        checked={formData.consent}
                        onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
                        required
                        className="w-5 h-5 mt-0.5 accent-[#C9A462]"
                        data-testid="investor-form-consent"
                      />
                      <label htmlFor="inv-consent" className="text-[13px] text-[#4E5359]">
                        {content.form.consent} <Link to={sl ? "/zasebnost" : "/privacy"} className="underline">{content.form.privacyLink}</Link>.
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 mt-4 bg-[#C9A462] text-[#0E1114] font-bold text-[16px] cursor-pointer hover:bg-[#B8944F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="investor-form-submit"
                    >
                      {isSubmitting ? content.form.submitting : content.form.submit}
                      {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <InvestorFaqAndCta faq={content.faq} cta={content.cta} onCta={scrollToForm} />

      <NewFooter />
      <ChatWidget />
    </div>
  );
};

export default InvestorsPage;
